import CloseOutlined from "@mui/icons-material/CloseOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import evaluateIcon from "assets/svg/evaluate.svg";
import gatewayStatusIcon from "assets/svg/gateway-status.svg";
import dateIcon from "assets/svg/on-date.svg";
import recurrenceIcon from "assets/svg/recurrence.svg";
import sensorStatusIcon from "assets/svg/sensor-status.svg";
import sensorValueIcon from "assets/svg/sensor-value.svg";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import SelectEventVariableModal from "components/events/modals/SelectEventVariableModal";
import LDSUSelectModal from "components/modals/LDSUSelectModal";
import DefaultModal from "components/modals/ModalTemplate";
import SensorActuatorSelectModal from "components/modals/SensorActuatorSelectModal";
import {
    DeviceTypeDict,
    EventConfigLimit,
    LDSUTypeDict
} from "constant";
import { EVENT_ENABLED_MODIFY_MESSAGE } from "constant/messages";
import { Condition, Variable } from "generated/models";
import { ChangeEvent, MouseEvent, useMemo, useState } from "react";
import { Button, Form, FormControl, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { Id } from "react-toastify";
import LocalStorageService from "service/localStorageService";
import { Event } from "types/Event";
import { Group, Registry } from "types/Gateways";
import { showErrorAlert } from "utils/alert";
import {
    isGatewayLevelEvent
} from "utils/eventFunctions";
import { isStandaloneGroup } from "utils/gatewayFunctions";
import { ConditionSlot } from "./components/ConditionSlot";

export type ConditionPanelProps = {
    event: Event;
    initialGatewayGroupList: Group[];

    eventVariables: Variable[];
    allGateways: Registry[];
    showDeleteConfirmation: (type: string, uuid: string) => Id | undefined;
};

const CONDITION_SENSOR_VALUE = "sensor-value";
const CONDITION_LDSU_STATUS = "ldsu-status";
const CONDITION_SENSOR_STATUS = "sensor-status";
const CONDITION_GATEWAY_STATUS = "gateway-status";
const CONDITION_RECURRENCE = "recurrence";
const CONDITION_ONDATE = "on-date";


const selectedSensorDetails = {
    name: "",
    gatewayName: "",
    bus: "",
    ldsuUUID: "",
    type: "",
    gatewayId: "",
    unit: "",
    said: "",
    min: "",
    max: "",
};

export const ConditionPanel = (props: ConditionPanelProps) => {
    const {
        event,
        initialGatewayGroupList,
        eventVariables,
        allGateways,
        showDeleteConfirmation,
    } = props;

    const params: any = useParams();
    const history = useHistory();
    const eventId = params.eventId;

    const metricCLSList: Record<string, {
        name: string;
        metric: string;
    }> = LocalStorageService.getItem("metricInfo").cls;
    const jsonList = LocalStorageService.getItem("listOfJSON") || [];

    const [showGuardWarning, setShowGuardWarning] = useState(false);
    const [selectCondition, setSelectCondition] = useState(false);
    const [unitType, setUnitType] = useState(DeviceTypeDict.sensor.label);
    const [selectedOperator, setSelectedOperator] = useState("");
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);
    const [selectedCondition, setSelectedCondition] = useState("");
    const [selectModalVisible, setSelectModalVisible] = useState(false);
    const [selectGateway, setSelectGateway] = useState(false);
    const [selectedGateway, setSelectedGateway] = useState("");
    const [radioBtnSelected, setRadioBtnSelected] = useState(false);
    const [gatewayGroupList, setGatewayGroupList] = useState<Group[]>([]);
    const [eventVariableSelected, setEventVariableSelected] = useState("");
    const [isShowEventVariables, setIsShowEventVariables] = useState(false);
    const isGatewayLevel = isGatewayLevelEvent(event);
    const slotMap = useMemo(() => {
        return event.conditions.reduce<Record<number, Condition[]>>(
            (acc, condition) => {
                const slot = condition.slot;
                if (typeof slot !== "number") {
                    return acc;
                }
                acc[slot] = [...(acc[slot] || []), condition];
                return acc;
            },
            {}
        );
    }, [event.conditions]);
    const orderedSlots = useMemo(() => {
        return Object.keys(slotMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map((slot) => slotMap[slot]);
    }, [slotMap]);

    const slotCount = useMemo(() => {
        return Math.max(...event.conditions.map((c: any) => c.slot)) + 1;
    }, [event.conditions]);

    const handleConditionsClose = () => setSelectCondition(false);

    const addToSameConditionGroup = (index: number) => {
        runIfEditable(() => {
            setSelectCondition(true);
            setUnitType(DeviceTypeDict.sensor.label);
            setSelectedOperator("AND");
            setSelectedSlotIndex(index);
        });
    };

    const checkModify = (
        e?: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>
    ) => {
        if (event.activate) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            showErrorAlert({
                message: EVENT_ENABLED_MODIFY_MESSAGE,
            });
            return false;
        }

        return true;
    };

    const runIfEditable = (action: () => void) => {
        if (!checkModify()) {
            return;
        }
        action();
    };

    const renderSlots = () => {
        const list = orderedSlots.map((slot: Condition[], index: number) => {
            return (
                <ConditionSlot
                    key={`condition-slot-wrapper-${slot[0].uuid}`}
                    eventId={eventId}
                    eventActivate={event.activate}
                    passiveSlots={event.passive_slots}
                    isGatewayLevel={isGatewayLevel}
                    slotIndex={index}
                    slot={slot}
                    allGateways={allGateways}
                    eventVariables={eventVariables}
                    metricCLSList={metricCLSList}
                    jsonList={jsonList}
                    showDeleteConfirmation={showDeleteConfirmation}
                    setShowGuardWarning={setShowGuardWarning}
                    addToSameConditionGroup={addToSameConditionGroup}
                />
            );
        });
        return list;
    };

    const showCondition = (condition?: string, config = {}) => {
        runIfEditable(() => {
            if (
                condition === CONDITION_ONDATE ||
                condition === CONDITION_RECURRENCE
            ) {
                history.push(
                    `/event-details/${params.eventId}/condition/${condition}`,
                    {
                        selectedOperator,
                        slotCount,
                        selectedSlotIndex,
                        isGatewayLevel,
                    }
                );
            } else {
                history.push(
                    `/event-details/${params.eventId}/condition/${selectedCondition}`,
                    {
                        selectedSensorDetails,
                        selectedOperator,
                        slotCount,
                        selectedSlotIndex,
                        selectedGateway,
                        isGatewayLevel,
                        // selectedGatewayName,
                        ...config,
                    }
                );
            }
        });
    };

    const closeGatewaySelection = () => {
        searchGateways("");
        setSelectGateway(false);
        setRadioBtnSelected(false);
    };

    const openDeviceSelection = (conditionType: string, nextUnitType: string) => {
        setUnitType(nextUnitType);
        setSelectedCondition(conditionType);
        handleConditionsClose();
        setSelectModalVisible(true);
    };

    const showLDSUSelection = () => {
        openDeviceSelection(CONDITION_LDSU_STATUS, LDSUTypeDict.LDSU.label);
    };

    const showSensorSelection = () => {
        openDeviceSelection(CONDITION_SENSOR_VALUE, DeviceTypeDict.sensor.label);
    };

    const showGatewaySelection = () => {
        setSelectedCondition(CONDITION_GATEWAY_STATUS);
        handleConditionsClose();
        setGatewayGroupList(initialGatewayGroupList);
        setSelectGateway(true);
    };

    const proceedToDateCondition = (conditionType: string) => {
        handleConditionsClose();
        showCondition(conditionType);
    };

    const proceedToRecurrencePage = () => {
        proceedToDateCondition(CONDITION_RECURRENCE);
    };

    const proceedToOnDatePage = () => {
        proceedToDateCondition(CONDITION_ONDATE);
    };

    const addFirstConditionGroup = () => {
        runIfEditable(() => {
            setSelectCondition(true);
            setUnitType(DeviceTypeDict.sensor.label);
        });
    };

    const addNewConditionGroup = () => {
        runIfEditable(() => {
            setSelectCondition(true);
            setUnitType(DeviceTypeDict.sensor.label);
            setSelectedOperator("OR");
        });
    };

    const searchGateways = (query: string) => {
        if (query === "") {
            setGatewayGroupList(initialGatewayGroupList);
            return;
        }

        const lowered = query.toLowerCase();
        const next = initialGatewayGroupList
            .map((group) => ({
                ...group,
                gateways: group.gateways.filter((g) =>
                    g.name.toLowerCase().includes(lowered)
                ),
            }))
            .filter((group) => group.gateways.length > 0);

        setGatewayGroupList(next);

    };





    const renderGatewayGroup = (group: any) => {

        const handleGatewayChange = (e: ChangeEvent<HTMLInputElement>) => {
            setSelectedGateway(e.target.value);
            setRadioBtnSelected(true);
        };

        const renderGatewayOption = (gateway: any) => {
            return (
                <div className="modal-option" key={gateway.uuid}>
                    <Form.Check
                        name="selected-gateway"
                        value={gateway.gateway_id}
                        onChange={handleGatewayChange}
                        id={gateway.gateway_id}
                        custom
                        type="radio"
                        label={
                            <div>
                                <h4>{gateway.name}</h4>{" "}
                                <p>UUID: {gateway.gateway_id}</p>
                            </div>
                        }
                        className="float-left"
                    />
                </div>
            );
        };

        return (
            <div key={group.uuid}>
                {isStandaloneGroup(group) ? (
                    <h4>Standalone</h4>
                ) : (
                    <h4>{group.name}</h4>
                )}
                {group.gateways.map(renderGatewayOption)}
            </div>
        );
    };

    const renderGateways = () => {
        return gatewayGroupList
            .filter((group: any) => group.gateways.length)
            .map(renderGatewayGroup);
    };

    const handleSelectEventVariable = () => {
        setIsShowEventVariables(false);
        if (!eventVariableSelected) {
            return;
        }
        runIfEditable(() => {
            history.push(
                `/event-details/${params.eventId}/condition/event-variable/${eventVariableSelected}`,
                {
                    selectedOperator,
                    slotCount,
                    selectedSlotIndex,
                }
            );
        });
        setEventVariableSelected("");
    };

    return (
        <>
            {/* If No Conditions to list  */}
            {event.conditions.length ? (
                null
            ) : (
                <div className="conditions-list-empty">
                    <p>Add conditions to trigger this event</p>
                    <HoverAuthorizeTooltip permission="event:create">
                        <Button
                            variant="outline-primary"
                            onClick={addFirstConditionGroup}
                            className="add-condition-action-btn"
                        >
                            + ADD CONDITION
                        </Button>
                    </HoverAuthorizeTooltip>
                </div>
            )}

            {/* If actions to list  */}
            <div className="conditions-list">
                {/* //////////////Condition Groups//////////////// */}
                <div className="condition-group">
                    {event.conditions.length ? renderSlots() : null}
                </div>

                {/* ////////////////New Condition Group////////////////// */}
                {event.conditions.length ? (
                    <Box>
                        <HoverAuthorizeTooltip permission="event:create">
                            <Button
                                variant="primary add-item"
                                onClick={addNewConditionGroup}
                                disabled={
                                    isGatewayLevel &&
                                    orderedSlots.length >=
                                    EventConfigLimit.CONDITION_SLOTS_MAX
                                }
                            >
                                +
                            </Button>
                        </HoverAuthorizeTooltip>
                        {isGatewayLevel && (
                            <Typography
                                variant="body1"
                                color="#828FAB"
                                className="mt-2"
                            >
                                Maximum {EventConfigLimit.CONDITION_SLOTS_MAX} OR
                                sections
                            </Typography>
                        )}
                    </Box>
                ) : (
                    null
                )}
            </div>

            {/* Select Condition Modal */}
            <Modal
                show={selectCondition}
                onHide={handleConditionsClose}
                animation={false}
                centered
            >
                <Modal.Header>
                    <Modal.Title>Add Condition</Modal.Title>
                    <Button
                        variant=""
                        className="close-button"
                        onClick={handleConditionsClose}
                    >
                        <CloseOutlined color="inherit" />
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    <Form className="condition-options-list">
                        <div className="scroll-list float-left">
                            <Box
                                className="modal-option"
                                onClick={showLDSUSelection}
                                onKeyDown={showLDSUSelection}
                            >
                                <img
                                    src={sensorStatusIcon}
                                    alt="sensor-status"
                                />
                                <div className="float-left">
                                    <h4>LDSU Status</h4>
                                    <p>E.g. “When LDSU goes offline”</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>

                            <Box
                                className="modal-option"
                                onClick={showSensorSelection}
                                onKeyDown={showSensorSelection}
                            >
                                <img src={sensorValueIcon} alt="sensor-value" />
                                <div className="float-left">
                                    <h4>Sensor Value</h4>
                                    <p>
                                        E.g. “When sensor value rises above
                                        30°C”
                                    </p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                display={isGatewayLevel ? "none" : "block"}
                                className="modal-option"
                                onClick={showGatewaySelection}
                                onKeyDown={showGatewaySelection}
                            >
                                <img
                                    src={gatewayStatusIcon}
                                    alt="gateway-status"
                                />
                                <div className="float-left">
                                    <h4>Gateway Status</h4>
                                    <p>E.g. “When gateway comes online”</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className="modal-option"
                                onClick={proceedToRecurrencePage}
                                onKeyDown={proceedToRecurrencePage}
                            >
                                <img src={recurrenceIcon} alt="recurrence" />
                                <div className="float-left">
                                    <h4>Recurrence</h4>
                                    <p>E.g. “Every Sunday and Monday”</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className="modal-option"
                                onClick={proceedToOnDatePage}
                                onKeyDown={proceedToOnDatePage}
                            >
                                <img
                                    className="float-left mr-3"
                                    src={dateIcon}
                                    alt="date"
                                />
                                <div className="float-left">
                                    <h4>On a Date</h4>
                                    <p>E.g. “On 1st of January”</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>

                            <Box
                                component="fieldset"
                                disabled={isGatewayLevel}
                                className="modal-option"
                                onClick={() => {
                                    handleConditionsClose();
                                    setIsShowEventVariables(true);
                                }}
                                onKeyDown={() => {
                                    handleConditionsClose();
                                    setIsShowEventVariables(true);
                                }}
                            >
                                <img
                                    className="float-left mr-3"
                                    src={evaluateIcon}
                                    alt="evaluate"
                                />
                                <div className="float-left">
                                    <h4>Evaluate Event Variable</h4>
                                    <p>
                                        E.g. “When Event Variable value less
                                        than 5”
                                    </p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Select Gateway Modal */}
            {CONDITION_GATEWAY_STATUS === selectedCondition && (
                <Modal
                    show={selectGateway}
                    onHide={closeGatewaySelection}
                    animation={false}
                    centered
                >
                    <Modal.Header>
                        <Modal.Title>Select Gateway</Modal.Title>
                        <Button
                            variant=""
                            className="close-button"
                            onClick={closeGatewaySelection}
                        >
                            <span className="material-icons">close</span>
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="floar-left">
                            <FormControl
                                type="text"
                                placeholder="Search.."
                                className="mb-4"
                                onChange={(e: any) => {
                                    searchGateways(e.target.value);
                                }}
                            ></FormControl>
                            <div className="scroll-multiple-list float-left">
                                {renderGateways()}
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        {radioBtnSelected ? (
                            <Button
                                variant="primary"
                                onClick={() => showCondition()}
                            >
                                {selectedCondition === CONDITION_GATEWAY_STATUS
                                    ? "DONE"
                                    : "NEXT"}
                            </Button>
                        ) : (
                            null
                        )}
                    </Modal.Footer>
                </Modal>
            )}

            {/* Select LDSU Modal */}
            {selectModalVisible && CONDITION_LDSU_STATUS === selectedCondition && (
                <LDSUSelectModal
                    {...{
                        gatewayId: event.gateway_id,
                        unitType,
                        isVisible: selectModalVisible,
                        onClose: () => {
                            setSelectModalVisible(false);
                        },
                        onFinish: (device: any) => {
                            if (unitType === LDSUTypeDict.LDSU.label) {
                                showCondition(undefined, {
                                    selectedSensorDetails: {
                                        ...selectedSensorDetails,
                                        name: device.name,
                                        ldsuUUID: device.ldsu_uuid,
                                        gatewayId: device.gatewayId,
                                        gatewayName: device.gatewayName,
                                        bus: device.bus,
                                    },
                                });
                            }
                        },
                    }}
                />
            )}

            {/* Sensor Value Modal */}
            {[CONDITION_SENSOR_VALUE, CONDITION_SENSOR_STATUS].includes(
                selectedCondition
            ) && (
                    <SensorActuatorSelectModal
                        gatewayId={event.gateway_id}
                        unitType={unitType}
                        isVisible={selectModalVisible}
                        onClose={() => {
                            setSelectModalVisible(false);
                        }}
                        onFinish={(device: any) => {
                            if (unitType === DeviceTypeDict.sensor.label) {
                                showCondition(undefined, {
                                    selectedSensorDetails: {
                                        ...selectedSensorDetails,
                                        name: device.sensorName,
                                        bus: device.name,
                                        ldsuUUID: device.ldsuUID,
                                        said: device.said,
                                        gatewayId: device.gatewayId,
                                        type: device.sensorType,
                                        gatewayName: device.gatewayName,
                                        unit: device.unit,
                                        min: device.min,
                                        max: device.max,
                                    },
                                });
                            }
                        }}

                    />
                )}

            <DefaultModal
                modalShow={showGuardWarning}
                setModalShow={setShowGuardWarning}
                modalType={"custom"}
                modalTitle="Error Details"
                modalButton={
                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowGuardWarning(false);
                        }}
                    >
                        OK
                    </Button>
                }
                modalContent={
                    "It must be guarded by adding an additional AND condition using a non-event variable or a non-time range condition to avoid triggering the event repeatedly."
                }
            />

            <SelectEventVariableModal
                isShowEventVariables={isShowEventVariables}
                setIsShowEventVariables={setIsShowEventVariables}
                eventVariableSelected={eventVariableSelected}
                eventVariables={eventVariables}
                handleSelectedEventVariable={handleSelectEventVariable}
                setEventVariableSelected={setEventVariableSelected}
                modalTitle="Select Event Variable"
            />
        </>
    );
};
